import { useState, useEffect } from "react";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TagInput } from "@/components/ui/tag-input";
import { SubscriptionPlanDB } from "@/services/subscriptionPlanService";

interface SubscriptionPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan?: SubscriptionPlanDB | null;
  onSave: (data: any) => Promise<void>;
}

export function SubscriptionPlanDialog({ open, onOpenChange, plan, onSave }: SubscriptionPlanDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    tier: "free" as "free" | "starter" | "pro" | "unlimited",
    price: 0,
    price_display: "€0",
    description: "",
    features: [] as string[],
    lead_limit: 0,
    is_recommended: false,
    is_active: true,
    duration_days: 30,
  });

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name,
        tier: plan.tier,
        price: plan.price,
        price_display: plan.price_display,
        description: plan.description || "",
        features: plan.features || [],
        lead_limit: plan.lead_limit,
        is_recommended: plan.is_recommended,
        is_active: plan.is_active,
        duration_days: plan.duration_days,
      });
    } else {
      setFormData({
        name: "",
        tier: "free",
        price: 0,
        price_display: "€0",
        description: "",
        features: [],
        lead_limit: 0,
        is_recommended: false,
        is_active: true,
        duration_days: 30,
      });
    }
  }, [plan, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = plan ? { id: plan.id, ...formData } : formData;
      await onSave(submitData);
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving plan:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{plan ? "Edit Subscription Plan" : "Create Subscription Plan"}</DialogTitle>
          <DialogDescription>
            {plan ? "Update the subscription plan details below." : "Add a new subscription plan to your system."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Starter Plan" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tier">Tier</Label>
                <Select value={formData.tier} onValueChange={(v: "free" | "starter" | "pro" | "unlimited") => setFormData({ ...formData, tier: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="unlimited">Unlimited</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price (cents)</Label>
                <Input id="price" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price_display">Price Display</Label>
                <Input id="price_display" value={formData.price_display} onChange={(e) => setFormData({ ...formData, price_display: e.target.value })} placeholder="e.g. €9.99" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lead_limit">Lead Limit</Label>
                <Input id="lead_limit" type="number" value={formData.lead_limit} onChange={(e) => setFormData({ ...formData, lead_limit: Number(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration_days">Duration (days)</Label>
                <Input id="duration_days" type="number" value={formData.duration_days} onChange={(e) => setFormData({ ...formData, duration_days: Number(e.target.value) })} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Plan description..." rows={3} />
            </div>

            <div className="space-y-2">
              <Label>Features</Label>
              <TagInput selected={formData.features} options={[]} onChange={(features) => setFormData({ ...formData, features })} placeholder="Add features..." creatable />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox id="is_recommended" checked={formData.is_recommended} onCheckedChange={(v) => setFormData({ ...formData, is_recommended: !!v })} />
                <Label htmlFor="is_recommended">Recommended</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="is_active" checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: !!v })} />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
          </DialogBody>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : plan ? "Update Plan" : "Create Plan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
